import { useSettings } from "../../contexts/SettingsManager/SettingsManager";
import { useDayStartChanged } from "store/atoms";
import { Autocomplete, TextField, IconButton, Tooltip } from "@mui/material";
import { ONE_HOUR_OF_MS, ONE_MINUTE_OF_MS } from "../../utils/timeUtils";
import styled, { createGlobalStyle, css } from "styled-components/macro";
import { Replay } from "@mui/icons-material";
import { useCallback, useMemo } from "react";
import {
  useDashboardPageContext,
  useFormatZoned,
  useGetPageFromPath,
  useSelectedDay,
  useSelectedShift,
} from "utils/customHooks/genericHooks";
import { CUSTOM_SCROLLBAR_CSS } from "App/CUSTOM_SCROLLBAR_CSS";
import { goToDay, goToShift } from "providers/GlobalStateProvider";
import { useDispatch } from "react-redux";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";

export const DayStartControls = () => {
  const { dayStartMsOffset, setDayStartMsOffset } = useSettings();
  return (
    <DayStartControlsContent {...{ dayStartMsOffset, setDayStartMsOffset }} />
  );
};

const hourOptions = Array.from({ length: 47 }, (_, i) => i - 23);

export function DayStartControlsContent({
  dayStartMsOffset,
  setDayStartMsOffset,
}: {
  dayStartMsOffset: number;
  setDayStartMsOffset: (dayStartMsOffset: number) => void;
}) {
  const [, setDayStartChanged] = useDayStartChanged();

  const minutes = Math.floor((dayStartMsOffset / ONE_MINUTE_OF_MS) % 60);
  const hours = Math.floor(
    (dayStartMsOffset - minutes * ONE_MINUTE_OF_MS) / ONE_HOUR_OF_MS
  );
  const adjustedMinutes = hours === 0 ? minutes : Math.abs(minutes);

  // if hour === 0, allow selecting negative minutes; otherwise only allow positive minutes
  const minuteOptions = useMemo(
    () =>
      hours === 0
        ? Array.from({ length: 119 }, (_, i) => i - 59)
        : Array.from({ length: 60 }, (_, i) => i),
    [hours]
  );
  const { isSettingsPage } = useGetPageFromPath();

  const recalculateShifts = useRecalculateShifts();

  return (
    <Styles $isSettingsPage={isSettingsPage}>
      <GlobalAutocompleteStyles />
      <div style={{ display: "flex", alignItems: "center" }}>
        <Autocomplete
          getOptionLabel={option => String(option)}
          options={hourOptions}
          value={hours}
          onChange={(event, newValue) => {
            if (newValue === null) {
              return;
            }
            const newDayStartOffsetMs =
              ONE_MINUTE_OF_MS * (Number(newValue) * 60 + adjustedMinutes);
            setDayStartMsOffset(newDayStartOffsetMs);
            setDayStartChanged(true);
            if (!isSettingsPage) {
              recalculateShifts();
            }
          }}
          renderInput={params => (
            <TextField
              {...params}
              style={{ width: isSettingsPage ? 64 : 48 }}
              data-testid="day-start-hour-input"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = Number(event.target.value);
                if (isNaN(newValue)) {
                  return;
                }
                const newDayStartOffsetMs =
                  ONE_MINUTE_OF_MS * (newValue * 60 + adjustedMinutes);
                setDayStartMsOffset(newDayStartOffsetMs);
                setDayStartChanged(true);
                if (!isSettingsPage) {
                  recalculateShifts();
                }
              }}
            />
          )}
          disableClearable
          {...(isSettingsPage
            ? {}
            : {
                popupIcon: null,
              })}
        />
        <span>h</span>
        <Autocomplete
          getOptionLabel={option => String(option)}
          options={minuteOptions}
          value={adjustedMinutes}
          onChange={(event, newValue) => {
            if (newValue === null) {
              return;
            }
            const newDayStartOffsetMs =
              ONE_MINUTE_OF_MS * (hours * 60 + Number(newValue));
            setDayStartMsOffset(newDayStartOffsetMs);
            setDayStartChanged(true);
            if (!isSettingsPage) {
              recalculateShifts();
            }
          }}
          renderInput={params => (
            <TextField
              {...params}
              style={{ width: isSettingsPage ? 64 : 48 }}
              data-testid="day-start-minute-input"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = Number(event.target.value);
                if (isNaN(newValue)) {
                  return;
                }
                const newDayStartOffsetMs =
                  ONE_MINUTE_OF_MS * (hours * 60 + newValue);
                setDayStartMsOffset(newDayStartOffsetMs);
                setDayStartChanged(true);
                if (!isSettingsPage) {
                  recalculateShifts();
                }
              }}
            />
          )}
          disableClearable
          {...(isSettingsPage
            ? {}
            : {
                popupIcon: null,
              })}
        />
        <span>m</span>
      </div>
      {dayStartMsOffset !== 0 && (
        <Tooltip title="Reset to 00:00">
          <IconButton
            className="btnResetDayStartOffset"
            onClick={() => {
              setDayStartMsOffset(0);
              recalculateShifts();
            }}
          >
            <Replay />
          </IconButton>
        </Tooltip>
      )}
    </Styles>
  );
}

export function useRecalculateShifts() {
  const dispatch = useDispatch();
  const formatZoned = useFormatZoned();
  const { isFullDaySelected } = useDashboardPageContext();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const selectedDay = useSelectedDay();
  const selectedShift = useSelectedShift();
  const { dayStartMsOffset } = useSettings();

  return useCallback(() => {
    if (isFullDaySelected) {
      dispatch(
        goToDay({
          newSelectedDay: selectedDay,
          dayStartMsOffset,
          timeZoneMsOffset,
          shouldGoToFirstShiftInDay: false,
          shouldGoToCurrentShift: false,
          formatZoned,
        })
      );
    } else {
      dispatch(
        goToShift(
          selectedShift,
          dayStartMsOffset,
          timeZoneMsOffset,
          formatZoned
        )
      );
    }
  }, [
    dispatch,
    formatZoned,
    isFullDaySelected,
    selectedDay,
    selectedShift,
    timeZoneMsOffset,
    dayStartMsOffset,
  ]);
}

const Styles = styled.div<{ $isSettingsPage?: boolean }>`
  position: relative;
  .btnResetDayStartOffset {
    position: absolute;
    width: 24px;
    height: 24px;
    .MuiSvgIcon-root {
      width: 16px;
      height: 16px;
    }
    top: -12px;
    right: -14px;
  }
  display: flex;
  .MuiInputBase-root {
    ${props =>
      !props.$isSettingsPage
        ? css`
            padding: 0;
            padding-right: 0px !important;
          `
        : ""}
  }
  input {
    text-align: right;
  }
`;

const GlobalAutocompleteStyles = createGlobalStyle`
  .MuiAutocomplete-listbox{
    overflow-x: hidden;
    ${CUSTOM_SCROLLBAR_CSS}
  }
  .MuiAutocomplete-option{
    padding-left: 8px !important;
  }
`;
